import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Mail, Settings, CreditCard, Radio, Globe2, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { DevistoLogo } from "@/components/DevistoLogo";
import { AdminBlog } from "@/components/AdminBlog";
import { AdminSecurity } from "@/components/AdminSecurity";
import { AdminDriveBackup } from "@/components/AdminDriveBackup";
import { AdminEinvoicing } from "@/components/AdminEinvoicing";
import { AdminSubscriptions } from "@/components/AdminSubscriptions";
import { AdminPeppol } from "@/components/AdminPeppol";
import { AdminDemoAccount } from "@/components/AdminDemoAccount";
import { AdminFiscal } from "@/components/AdminFiscal";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "subscriptions", label: "Abonnements", icon: CreditCard },
  { id: "peppol", label: "Peppol", icon: Radio },
  { id: "fiscal", label: "Fiscalité", icon: Globe2 },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "messages", label: "Messages", icon: Mail },
  { id: "settings", label: "Réglages", icon: Settings },
];

export default function AdminDashboard() {
  const { t } = useLanguage();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "subscriptions");
  const [messages, setMessages] = useState([]);
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "", until: "" });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("devisto_admin_token");

  const logout = useCallback(() => {
    localStorage.removeItem("devisto_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([axios.get(`${API}/admin/messages`, { headers }), axios.get(`${API}/maintenance`)])
      .then(([m, mt]) => {
        setMessages(m.data);
        setMaintenance({ enabled: mt.data.enabled, message: mt.data.message || "", until: mt.data.until || "" });
      })
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [token, navigate, logout]);

  if (loading) {
    return (
      <div className="font-body bg-slate-950 text-[#93A0BC] min-h-screen flex items-center justify-center">
        Chargement…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="font-body bg-slate-950 text-slate-50 min-h-screen">
      <header className="border-b border-slate-800 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="admin-logo"><DevistoLogo className="h-9 w-auto" variant="white" /></Link>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:border-slate-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
          >
            <LogOut className="w-4 h-4" /> {t("Déconnexion")}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{t("Tableau de bord")}</h1>
        <p className="mt-2 text-sm text-[#93A0BC]">Devisto Studio — abonnements, Peppol et messages de contact.</p>

        <div className="mt-8 flex flex-wrap gap-3">
          {TABS.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              data-testid={`tab-${item.id}`}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] ${
                tab === item.id ? "border-slate-500 bg-slate-800 text-white" : "border-slate-700 text-[#93A0BC] hover:border-slate-500"
              }`}
            >
              <item.icon className="w-4 h-4" /> {t(item.label)}
              {item.id === "messages" ? ` (${messages.length})` : ""}
            </button>
          ))}
        </div>

        {tab === "subscriptions" && <AdminSubscriptions token={token} />}

        {tab === "peppol" && <AdminPeppol token={token} />}
        {tab === "fiscal" && <AdminFiscal token={token} />}
        {tab === "blog" && <AdminBlog token={token} />}

        {tab === "settings" && <AdminDemoAccount headers={{ Authorization: `Bearer ${token}` }} />}
        {tab === "settings" && <AdminSecurity token={token} />}
        {tab === "settings" && <AdminDriveBackup token={token} />}
        {tab === "settings" && <AdminEinvoicing token={token} />}
        {tab === "settings" && (
          <div data-testid="admin-maintenance" className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 sm:p-8 max-w-xl">
            <h2 className="font-heading text-xl font-semibold">{t("Mode maintenance")}</h2>
            <p className="mt-2 text-sm text-[#93A0BC] leading-relaxed">
              {t("Activez ce mode pour afficher une page « Site en maintenance » aux visiteurs. Les administrateurs, la page de connexion, les liens de suivi client et les passeports QR restent accessibles.")}
            </p>
            <div className="mt-5 space-y-3">
              <label className="flex items-center gap-3 text-sm text-slate-200">
                <input
                  type="checkbox"
                  data-testid="maintenance-toggle"
                  checked={maintenance.enabled}
                  onChange={(e) => setMaintenance((m) => ({ ...m, enabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-600 bg-slate-950 accent-amber-500"
                />
                {maintenance.enabled ? t("Site en maintenance (visiteurs bloqués)") : t("Site en ligne")}
              </label>
              <input
                data-testid="maintenance-message"
                value={maintenance.message}
                onChange={(e) => setMaintenance((m) => ({ ...m, message: e.target.value }))}
                placeholder={t("Message affiché aux visiteurs (facultatif)")}
                className="w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                data-testid="maintenance-until"
                value={maintenance.until}
                onChange={(e) => setMaintenance((m) => ({ ...m, until: e.target.value }))}
                placeholder={t("Retour prévu (ex. : lundi 8 h)")}
                className="w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="maintenance-save-btn"
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/admin/maintenance`, maintenance, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(maintenance.enabled ? t("Mode maintenance activé.") : t("Site remis en ligne."));
                    } catch (e) {
                      toast.error(t("Enregistrement impossible."));
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-[#0F2645] hover:bg-amber-400 transition-colors duration-200"
                >
                  {t("Enregistrer")}
                </button>
                <a href="/?apercu=1" target="_blank" rel="noopener noreferrer" data-testid="maintenance-preview-link" className="text-xs font-semibold text-amber-300 hover:text-amber-200 transition-colors">
                  {t("Ouvrir le site en contournant la maintenance")} →
                </a>
              </div>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div data-testid="admin-messages-list" className="mt-8 space-y-4">
            {messages.length === 0 && <p className="text-sm text-[#5C6A87]">{t("Aucun message pour le moment.")}</p>}
            {messages.map((m) => (
              <article key={m.id} data-testid={`message-card-${m.id}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-heading text-lg font-semibold">{m.subject}</h2>
                  <span className="text-xs text-[#5C6A87]">{new Date(m.created_at).toLocaleDateString("fr-BE")}</span>
                </div>
                <p className="mt-2 text-sm text-[#93A0BC] leading-relaxed">{m.message}</p>
                <p className="mt-3 text-sm text-slate-300">
                  {m.name} · <a href={`mailto:${m.email}`} className="text-[#9FB4F2] hover:text-white">{m.email}</a>
                </p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
